import { z } from 'zod'

/** Shared password policy — keep in sync with backend `App\Support\Auth\PasswordRules`. */
export const PASSWORD_MIN_LENGTH = 8

export const PASSWORD_HINT =
  'At least 8 characters with uppercase, lowercase, a number, and a special character.'

const hasUppercase = /[A-Z]/
const hasLowercase = /[a-z]/
const hasNumber = /[0-9]/
const hasSpecial = /[^A-Za-z0-9]/

export function passwordMeetsPolicy(value: string): boolean {
  return (
    value.length >= PASSWORD_MIN_LENGTH &&
    hasUppercase.test(value) &&
    hasLowercase.test(value) &&
    hasNumber.test(value) &&
    hasSpecial.test(value)
  )
}

/** Zod schema for any field that sets a new password. */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .refine((value) => hasUppercase.test(value), {
    message: 'Password must include an uppercase letter',
  })
  .refine((value) => hasLowercase.test(value), {
    message: 'Password must include a lowercase letter',
  })
  .refine((value) => hasNumber.test(value), {
    message: 'Password must include a number',
  })
  .refine((value) => hasSpecial.test(value), {
    message: 'Password must include a special character',
  })

const passwordConfirmationField = z.string().min(1, 'Please confirm your password')

/** Admin / set-password flows: password + confirmation only. */
export const setPasswordFormSchema = z
  .object({
    password: passwordSchema,
    password_confirmation: passwordConfirmationField,
  })
  .refine((values) => values.password === values.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

/** Profile update-credentials: current + new + confirmation. */
export const changePasswordFormSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    password: passwordSchema,
    password_confirmation: passwordConfirmationField,
  })
  .refine((values) => values.password === values.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })
